import React, { useState } from 'react';
import { Button, Modal, ModalHeader, ModalBody, Row, Col, Alert, Input, Label, FormGroup, InputGroup, InputGroupAddon } from 'reactstrap';

import { assert } from '../base/utils/assert';
import C from '../C';
import DataStore, { getDataPath } from '../base/plumbing/DataStore';
import ActionMan from '../plumbing/ActionMan';
import { getId } from '../base/data/DataClass';
import KStatus from '../base/data/KStatus';
import { setWindowTitle } from '../base/plumbing/Crud';
import NGO from '../base/data/NGO';
import { getXeroContactUrl } from '../data/Xero';

import SubCard from '../base/components/SubCard';
import Misc from '../base/components/Misc';
import PropControl from '../base/components/PropControl';
import ListLoad, { CreateButton } from '../base/components/ListLoad';
import ErrAlert from '../base/components/ErrAlert';
import SavePublishDeleteEtc from '../base/components/SavePublishDeleteEtc';
import { MondayLink } from './monday';
import NGOImageListControls from './NGOImageControls';
import LinkOut from '../base/components/LinkOut';
import { A } from '../base/plumbing/glrouter';
import Icon from '../base/components/Icon';
import { space } from '../base/utils/miscutils';


export function CreateNGOForm({ createThen = null }) {
	const [newId, setNewId] = useState('');
	const handleId = (e) => setNewId(e.target.value.replaceAll(' ', '-'));

	return <>
		<Alert color="warning">
			Always search <A href="#ngo">the NGO list</A> first to check the charity isn't already in the database.
			Otherwise we will have ugly merge problems.
		</Alert>
		<Label htmlFor="ngo-id">Input new NGO ID to continue: </Label>
		<Input className="w-50" id="ngo-id" placeholder="e.g. world-food-programme" onChange={handleId} />
		<small>{newId ? <>Normalised ID: <code>{newId}</code></> : '&nbsp;'}</small>
		<CreateButton then={createThen} disabled={!newId.length} className="btn-danger" type={C.TYPES.NGO} id={newId}>
			Create new NGO / Charity
		</CreateButton>
	</>;
}


function CreateNGOModal() {
	const [openModal, setOpenModal] = useState(false);
	const toggleModal = () => setOpenModal(!openModal);

	return <>
		<Button color="secondary" onClick={toggleModal}>Create NGO</Button>
		<Modal isOpen={openModal} toggle={toggleModal}>
			<ModalHeader>Caution</ModalHeader>
			<ModalBody><CreateNGOForm createThen={toggleModal} /></ModalBody>
		</Modal>
	</>;
}


function NGOPage() {
	const type = C.TYPES.NGO;
	setWindowTitle(type);

	const blocked = Misc.CheckAccess({ can: 'editAdvert' });
	if (blocked) return blocked;

	const path = DataStore.getValue(['location', 'path']);
	const ngoId = path[1];

	if (!ngoId) {
		return <>
			<CreateNGOModal />
			<ListLoad type={type} sort="" canFilter otherParams={{ unlisted: true }} status={KStatus.ALL_BAR_TRASH} />
		</>;
	}

	const pvItem = ActionMan.getDataItem({ type: C.TYPES.NGO, id: ngoId, status: KStatus.DRAFT, swallow: true });
	if (!pvItem.value) {
		return <div>
			<h1>NGO: {ngoId}</h1>
			{!pvItem.resolved && <Misc.Loading />}
			{pvItem.error && String(pvItem.error).includes('404') && (
				<Misc.Card title="Add a New Charity?">
					<CreateNGOForm />
				</Misc.Card>
			)}
			{pvItem.error && <ErrAlert color="danger" error={pvItem.error} />}
		</div>;
	}

	const item = pvItem.value;
	setWindowTitle(item);

	return <div>
		<h1>NGO: {item.name || ngoId}</h1>
		<EditNGO ngo={item} />
	</div>;
} // ./NGOPage


/**
 * Mark differences between a canonical version of an item and the copy currently being edited.
 * @param {Object} p
 * @param {String[]} p.path DataStore path for the currently-editing item
 * @param {String[]} p.referencePath DataStore path for the item version to compare against
 * @param {String} p.prop Name of the field to compare between versions
 */
function PropControlCompare({referencePath, className, ...pcProps}) {
	// No comparison to make
	if (!referencePath) return <PropControl {...pcProps} />;
	
	const {path, prop} = pcProps;
	const pcVal = DataStore.getValue(path.concat(prop));
	const refVal = DataStore.getValue(referencePath.concat(prop));

	let diff;
	if ((pcVal || refVal) && pcVal !== refVal) {
		const revert = () => DataStore.setValue(path.concat(prop), refVal);
		const displayRefVal = refVal || <span className="text-muted">(no value)</span>;
		diff = <FormGroup className="reference-version ml-2">
				<Label>Main version value:</Label>
				<InputGroup>
					<span className="form-control" title={refVal}>{displayRefVal}</span>
					<InputGroupAddon addonType="append"><Button size="sm" onClick={revert}>Revert</Button></InputGroupAddon>
				</InputGroup>
				{/* <Button size="xs" color="danger" onClick={makeGlobal}>Update main version</Button> */}
		</FormGroup>;
	}

	// Mark the primary version
	className = space(className, 'editing-version');

	return <div className="prop-control-compare flex-row">
		<PropControl {...pcProps} className={className} />
		{diff}
	</div>;
}


/**
 * Editor for either an NGO with its own item entry in the store, or an NGO-shaped sub-item.
 * @param {Object} p Must contain one of p.ngo or p.path
 * @param {NGO} [p.ngo] The NGO item this editor should act on. If present, this editor will automatically save draft changes.
 * @param {String[]} [p.path] The path to the NGO-shaped sub-item this editor should act on
 * @returns {React.Element}
 */
function EditNGO({ ngo, path }) {
	assert(ngo || path, 'EditNGO must receive either an NGO object or a DataStore path');
	// Pull out ID, name, xero ID, charity category for non-PropControl use
	const dsNGO = path && DataStore.getValue(path);
	const id = getId(ngo || dsNGO);
	const { name, xero, category } = (ngo || dsNGO || {});

	// Fill out common PropControl props
	if (ngo) path = getDataPath({ status: KStatus.DRAFT, type: C.TYPES.NGO, id });
	const common = { path };
	if (ngo) common.item = ngo;
	if (path) common.referencePath = getDataPath({ status: KStatus.PUBLISHED, type: C.TYPES.NGO, id });
	const t4gCommon = { ...common, path: path.concat('t4gTheme') };

	return (
		<div className="form ngo-editor">
			ID: {id}<br />
			<h4> <Misc.Thumbnail /> {name || id} </h4>
			{ngo && <SavePublishDeleteEtc type={C.TYPES.NGO} id={id} />}
			<h3>Details</h3>
			<PropControlCompare {...common} label="NGO Name" prop="name" />
			<PropControlCompare {...common} label="Display Name" prop="displayName" />
			<MondayLink board="2506405019" type="Charity" />

			<PropControlCompare {...common} prop="xero" label="Xero ID" placeholder="abc12345-ab12-34ab-ab12-12345abc" />
			{xero && <LinkOut href={getXeroContactUrl(ngo.xero)} >xero</LinkOut>}

			<RegNumEditor path={path} />

			<PropControlCompare {...common} prop="unlisted" type="checkbox" label="Unlisted" help="This hides the NGO from being listed as a public charity in SoGive's database. Use this for entries which are not really charities - e.g. projects." />

			{/* NB: the contact is managed in the CRM */}
			<PropControlCompare {...common} prop="notes" type="textarea" label="Notes" />
			<PropControlCompare {...common} prop="url" type="url" label="URL" />
			<PropControlCompare {...common} prop="logo" type="imgUpload" label="Logo" bg="white" version="standard" cacheControls circleCrop />
			<PropControlCompare {...common} prop="altlogo" type="imgUpload" label="Alternative Logo" bg="white" version="standard" cacheControls circleCrop />

			<h3>Descriptions</h3>
			<PropControlCompare {...common} prop="description" type="textarea" label="Description" />
			<PropControlCompare {...common} prop="summaryDescription" type="textarea" label="Summary Description" />
			<PropControlCompare {...common} prop="extendedDescription" type="textarea" label="Extended Description" help="For extra information about what the charity does. Only used in special cases (e.g. the T4G charity landing page)" />
			<PropControlCompare {...common} prop="quote" type="textarea" label="Quote" help="e.g. thank you note from charity spokeperson." />

			<h3>Images</h3>
			<PropControlCompare {...common} prop="images" type="imgUpload" label="Main Photo" bg="white" help="The main photo for this charity. Will have the most useage and will be the fallback for other images." version="standard" cacheControls />
			<PropControlCompare {...common} prop="headerImage" type="imgUpload" label="Header Photo" bg="white" help="For dedicated pages or sections requiring a fancy header" version="standard" cacheControls />
			<NGOImageListControls {...common} prop="imageList" ngo={ngo} title="Content images" desc="Some page designs require multiple images from a charity. They will appear in order." />

			<h3>Tabs for Good Theme</h3>
			<PropControlCompare {...t4gCommon} prop="t4gLogo" type="imgUpload" label="Tabs for Good Logo" help="Replace the default logo with another style, e.g. for a recolor. Must match!" dflt="https://my.good-loop.com/img/newtab/logo/white.png" />
			<PropControlCompare {...t4gCommon} prop="backgroundColor" type="color" label="Background Color" help="A solid color for the background. Will be replaced by backdrop images, if any exist" />
			<NGOImageListControls {...t4gCommon} prop="backdropImages" ngo={ngo} title="Backdrop images" desc="Images to display in the Tabs For Good background - must be high res! (1920x1080 minimum)" />

			<h3>Extra Information</h3>
			<PropControlCompare {...common} prop="category" type="select" label="Category" options={Object.keys(NGO.CATEGORY)} />
			<PropControlCompare {...common} prop="subcategory" type="select" label="Sub-Category" disabled={!category} options={category ? NGO.CATEGORY[category] : []} />
			<PropControlCompare {...common} prop="unsdg" type="select" label="UN Sustainable Development Goal (SDG)"
				options={[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17]}
				labels={NGO.UNSDGs}
				help="Which UN SDG does this charity mainly work on?"
			/>

			<PropControlCompare {...common} prop="color" type="color" label="Main Colour" />
			<PropControlCompare {...common} prop="txtColor" type="color" label="Text Colour" help="Text colour (that stands out on a backdrop that uses the main charity colour)" />
		</div>
	);
}


/** Path for the RegNumEditor PropControls, which don't bind directly to a data-object */
const tempPath = ['widget', 'EditCharity'];


/**
 * Presents inputs for regulating body (eg OSCR) and charity registration number, to edit the regulator->ID mapping in charity.regNums
 * @param {String[]} path The DataStore path where the charity is stored
*/
function RegNumEditor({path: basePath}) {
	const charity = DataStore.getValue(basePath);
	const path = basePath.concat('regNums');

	const regNums = NGO.regNums(charity);
	const { regBody, regNum } = DataStore.getValue(tempPath) || {};
	const alreadyPresent = regNums[regBody];
	const canAdd = regBody && regNum && !alreadyPresent;

	// Add the regulator + number to the mapping and clear the inputs
	const addRegNum = () => {
		if (!canAdd) return;
		DataStore.setValue(path.concat(regBody), regNum);
		DataStore.setValue(tempPath, {});
	};

	const deleteRegNum = (forBody) => {
		delete regNums[forBody];
		DataStore.setValue(path, regNums);
		if (NGO.KRegOrg.has(forBody)) {
			NGO.setOldRegNum(charity, forBody, null);
		}
	};

	return <SubCard>
		<p>Registration numbers -- most charities only have one, though international charities may be registered in several regions.</p>
		{regNums && Object.keys(regNums).map(rb => <>
			<hr/>
			<Row>
				<Col md={4}><p>{NGO.regLabel(rb)}</p></Col>
				<Col md={4}><PropControl path={path} prop={rb} type="text" inline /></Col>
				<Col md={4}><Button size="xs" color="danger" onClick={() => deleteRegNum(rb)}><Icon name="x" /></Button></Col>
			</Row>
		</>)}
		<hr/>
		<PropControl label="Registration body" type="select" path={tempPath} prop="regBody"
			options={NGO.KRegOrg.values} labels={NGO.REG_ORG_LABELS}
		/>
		<PropControl label="Registration number" type="text" path={tempPath} prop="regNum"/>
		<Button onClick={addRegNum} className="align-self-start" disabled={!canAdd}>+</Button>
		{alreadyPresent && <Alert color="warning">This charity already has a registration number with {regBody}.</Alert>}
	</SubCard>;
}


export default NGOPage;
export { EditNGO };
